import { useState, useEffect, useRef, useCallback } from "react";
import { Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { ClubResult, WizardState } from "./types";

interface Props {
  state: WizardState;
  onNext: (updates: Partial<WizardState>) => void;
}

export default function StepClubSearch({ state, onNext }: Props) {
  const [query, setQuery] = useState("");
  const [clubs, setClubs] = useState<ClubResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClub, setSelectedClub] = useState<ClubResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Wenn der Nutzer zurückkommt: Verein und Mannschaftsliste erneut laden.
  useEffect(() => {
    if (!state.club) return;
    setQuery(state.club.clubName);
    fetch(`https://api.torflash.de/api/bfv-search?q=${encodeURIComponent(state.club.clubName)}`)
      .then((r) => r.json())
      .then((data) => {
        const match = (data.clubs || []).find(
          (c: ClubResult) => c.clubPermanentId === state.club!.clubPermanentId,
        );
        setSelectedClub(
          match ?? {
            clubName: state.club!.clubName,
            clubPermanentId: state.club!.clubPermanentId,
            logoUrl: state.club!.logoUrl,
            address: state.club!.address,
            teams: state.clubTeams ?? [],
          },
        );
      })
      .catch(() => {
        setSelectedClub({
          clubName: state.club!.clubName,
          clubPermanentId: state.club!.clubPermanentId,
          logoUrl: state.club!.logoUrl,
          address: state.club!.address,
          teams: state.clubTeams ?? [],
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchClubs = useCallback(async (q: string) => {
    if (q.length < 2) {
      setClubs([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `https://api.torflash.de/api/bfv-search?q=${encodeURIComponent(q)}`,
        { signal: controller.signal },
      );
      if (!res.ok) throw new Error("server");
      const data = await res.json();
      setClubs(data.clubs || []);
    } catch (err: any) {
      if (err.name === "AbortError") return;
      setError("Verbindungsproblem. Versuch's nochmal.");
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClub) return;
    const timer = setTimeout(() => searchClubs(query), 350);
    return () => clearTimeout(timer);
  }, [query, searchClubs, selectedClub]);

  const handleSelectClub = (club: ClubResult) => {
    setSelectedClub(club);
    setQuery(club.clubName);
    setClubs([]);
  };

  const handleClearClub = () => {
    setSelectedClub(null);
    setQuery("");
  };

  const handleNext = () => {
    if (!selectedClub) return;
    const sameClub = state.club?.clubPermanentId === selectedClub.clubPermanentId;
    onNext({
      club: {
        clubName: selectedClub.clubName,
        clubPermanentId: selectedClub.clubPermanentId,
        logoUrl: selectedClub.logoUrl,
        address: selectedClub.address,
      },
      clubTeams: selectedClub.teams ?? [],
      ...(sameClub
        ? {}
        : {
            teams: [],
            branding: {
              ...state.branding,
              logoDataUrl: null,
            },
          }),
    });
  };

  const showHint = query.length > 0 && query.length < 2 && !selectedClub;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
          Such deinen Verein
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Die Mannschaften wählst du später aus. Wir holen sie automatisch beim BFV.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (selectedClub) handleClearClub();
          }}
          placeholder="Vereinsname eingeben..."
          className="h-14 rounded-xl pl-12 pr-12 text-base shadow-sm"
        />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {showHint && <p className="text-sm text-muted-foreground">Mindestens 2 Buchstaben</p>}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => searchClubs(query)}>
            Nochmal versuchen
          </Button>
        </div>
      )}

      {!loading && !error && query.length >= 2 && clubs.length === 0 && !selectedClub && (
        <p className="text-sm text-muted-foreground">
          Kein Verein gefunden. Schreib uns:{" "}
          <a href="mailto:info@torflash.de" className="text-primary hover:underline">
            info@torflash.de
          </a>
        </p>
      )}

      {!selectedClub && clubs.length > 0 && (
        <div className="space-y-3">
          {clubs.map((club) => (
            <button
              key={club.clubPermanentId}
              onClick={() => handleSelectClub(club)}
              className="w-full rounded-xl border border-border bg-card p-4 text-left transition-all hover:border-primary/40 hover:shadow-sm"
            >
              <div className="flex items-center gap-3">
                {club.logoUrl ? (
                  <img
                    src={club.logoUrl}
                    alt={club.clubName}
                    className="h-10 w-10 rounded-lg object-contain"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted font-display text-sm font-bold text-muted-foreground">
                    {club.clubName.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-foreground">{club.clubName}</p>
                  <p className="text-xs text-muted-foreground">{club.address}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedClub && (
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-3">
            {selectedClub.logoUrl ? (
              <img
                src={selectedClub.logoUrl}
                alt={selectedClub.clubName}
                className="h-12 w-12 rounded-lg object-contain"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 font-display text-lg font-bold text-primary">
                {selectedClub.clubName.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <p className="font-display text-lg font-bold text-foreground">
                {selectedClub.clubName}
              </p>
              <p className="text-sm text-muted-foreground">{selectedClub.address}</p>
            </div>
            <button
              onClick={handleClearClub}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Ändern
            </button>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            {selectedClub.teams?.length
              ? `${selectedClub.teams.length} Mannschaften gefunden. Auswahl folgt in Schritt 4.`
              : "Keine Mannschaften gefunden."}
          </p>

          <Button variant="hero" className="mt-4 w-full" onClick={handleNext}>
            Weiter →
          </Button>
        </div>
      )}
    </div>
  );
}
